import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BadgeCheck, Check, MessageCircle } from 'lucide-react';
import { Button, Card, Checkbox, Field, Input, message } from '../../ui';
import { PhoneInput } from '../../components/PhoneInput';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { FarmerPlaceFields, type FarmerPlace } from './FarmerPlaceFields';
import { FarmerWelcomeCard } from './FarmerWelcomeCard';
import { validateFarmerForm } from './WelcomePage';

/**
 * "REGISTER AS A FARMER" — the door for someone who has not signed in at all.
 *
 * The signed-in door (`/welcome`) asks for the number FIRST, because the person is already sitting on
 * a login. Here they have announced what they are, so the form reads in the order a person would say
 * it: this is my name, this is my number, here is the code you sent, and this is where I farm.
 *
 * The number is verified INSIDE the form. Nothing below it appears until the code is accepted,
 * because until then we do not know who is filling it in — and the place they give is about to be
 * turned into a territory and a sales person's book.
 *
 * A number we already hold is turned away at the "Send code" step, not after: the server refuses
 * `purpose: 'register'` for any known mobile (see AuthService.assertNotAlreadyRegistered), so an
 * existing farmer, dealer or staff member is told to sign in instead of being sent a pointless code.
 */

type Step = 'identify' | 'verify' | 'place';

export function RegisterFarmerPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const setTokens = useAuthStore((s) => s.setTokens);
  const syncUser = useAuthStore((s) => s.syncUser);

  /*
   * Somebody who ARRIVES signed in has no business here: a farmer mid-onboarding belongs on /welcome
   * and anyone else is already registered as something.
   *
   * Judged on how they arrived, not on what they are now — because confirming the code signs them in
   * as part of THIS form. Reading the live value bounced the person to /welcome the instant their
   * number was verified, half way through the flow they had chosen.
   */
  const arrivedSignedIn = useRef(!!user);
  if (arrivedSignedIn.current) return <Navigate to={user?.onboardingRequired ? '/welcome' : '/home'} replace />;
  return <RegisterForm navigate={navigate} qc={qc} setTokens={setTokens} syncUser={syncUser} />;
}

function RegisterForm({ navigate, qc, setTokens, syncUser }: any) {
  const [step, setStep] = useState<Step>('identify');
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [place, setPlace] = useState<FarmerPlace>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<any | null>(null);
  const [support, setSupport] = useState<any>(null);

  // Once verified, the answers are saved as they are typed — the same draft the office's call list
  // reads, so someone who abandons this form half-way is still a person we can ring.
  const lastSaved = useRef('');
  useEffect(() => {
    if (step !== 'place') return;
    const body = JSON.stringify({ fullName, ...place });
    if (body === lastSaved.current) return;
    const t = window.setTimeout(() => {
      lastSaved.current = body;
      request({ url: '/onboarding/draft', method: 'PATCH', data: { fullName, ...place, platform: 'WEB' } }).catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(t);
  }, [step, fullName, place]);

  const sendCode = async () => {
    const name = fullName.trim();
    const local: Record<string, string> = {};
    if (!name) local.name = 'Enter your full name.';
    if (!mobile || mobile.replace(/\D/g, '').length < 10) local.mobile = 'Enter your mobile number.';
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const r = await request<any>({ url: '/auth/login/otp/request', method: 'POST', data: { mobile, purpose: 'register' } });
      setDevCode(r?.devCode ?? null);
      setCode(r?.devCode ?? '');
      setStep('verify');
    } catch (e: any) {
      const err = e?.response?.data?.error;
      // The one refusal worth its own affordance: they already have an account.
      if (err?.code === 'ALREADY_REGISTERED') setErrors({ mobile: 'This number is already registered. Please sign in instead.' });
      else message.error(err?.message ?? 'Could not send the code. Please try again.');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length < 4) { setErrors({ code: 'Enter the code we sent you.' }); return; }
    setBusy(true);
    try {
      const r = await request<any>({
        url: '/auth/login/otp/verify', method: 'POST',
        data: { mobile, code: code.trim(), clientType: 'WEB' },
      });
      setTokens(r.accessToken, r.refreshToken, {
        id: r.user.id, fullName: r.user.fullName, role: r.user.role,
        roles: r.user.roles ?? [r.user.role], permissions: r.user.permissions,
        territoryId: r.user.territoryId, onboardingRequired: !!r.user.onboardingRequired,
      });
      // The name they gave above is theirs from this moment — saved before they touch anything else,
      // so an abandoned form still reaches the office with a name on it.
      request({ url: '/onboarding/draft', method: 'PATCH', data: { fullName: fullName.trim(), platform: 'WEB' } }).catch(() => undefined);
      request<any>({ url: '/onboarding/me' }).then((m) => setSupport(m?.support ?? null)).catch(() => undefined);
      setErrors({});
      setStep('place');
    } catch (e: any) {
      const err = e?.response?.data?.error;
      setErrors({ code: err?.message ?? 'That code did not work.' });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    const local = validateFarmerForm({ fullName, ...place }, consent);
    setErrors(local as Record<string, string | undefined>);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      setDone(await request<any>({
        url: '/onboarding/complete', method: 'POST',
        data: { fullName, ...place, consent: true, platform: 'WEB' },
      }));
    } catch (e: any) {
      const err = e?.response?.data?.error;
      if (err?.code === 'VALIDATION_FAILED' && err.details) setErrors(err.details);
      message.error(err?.message ?? 'Could not complete your registration. Please try again.');
    } finally { setBusy(false); }
  };

  const enterApp = async () => {
    try {
      const fresh = await request<any>({ url: '/auth/me' });
      syncUser({
        fullName: fresh.fullName, role: fresh.role, roles: fresh.roles ?? [fresh.role],
        permissions: fresh.permissions ?? [], onboardingRequired: !!fresh.onboardingRequired,
      });
      qc.invalidateQueries({ queryKey: ['auth-me'] });
    } catch { syncUser({ onboardingRequired: false }); }
    navigate('/home', { replace: true });
  };

  const verified = step === 'place';

  return (
    <div className="min-h-screen bg-surface-2 px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 flex items-center justify-between">
          <img src="/logo.png" alt="Green Bay Biotech International" className="h-12 object-contain" />
          {!done && (
            <Button variant="ghost" size="sm" icon={<ArrowLeft size={16} />} onClick={() => navigate('/')}>Back</Button>
          )}
        </div>

        {done ? (
          <FarmerWelcomeCard done={done} support={support} onContinue={enterApp} />
        ) : (
          <Card>
            <h1 className="type-section-title text-content">Register as a farmer</h1>
            <p className="mt-1 type-body text-muted">
              A few details, so your local Green Bay representative can reach you. It takes about a minute.
            </p>

            <div className="mt-5 flex flex-col gap-4">
              <Field label="Full name" required htmlFor="rf-name" error={errors.name}>
                <Input id="rf-name" autoComplete="name" value={fullName} invalid={!!errors.name} disabled={verified}
                  placeholder="e.g. Ramesh Patil" maxLength={120}
                  onChange={(e) => { setFullName(e.target.value); setErrors((x) => ({ ...x, name: undefined })); }} />
              </Field>

              <Field label="Mobile number" required error={errors.mobile}
                hint={verified ? undefined : 'We send a one-time code to this number on WhatsApp.'}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <PhoneInput value={mobile} onChange={(v) => { setMobile(v ?? ''); setErrors((x) => ({ ...x, mobile: undefined })); }}
                      placeholder="Mobile number" disabled={step !== 'identify'} />
                  </div>
                  {verified ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-control bg-success-500/12 px-3 py-2 type-label-strong text-success-700 dark:text-success-300">
                      <BadgeCheck size={15} aria-hidden /> Verified
                    </span>
                  ) : (
                    <Button variant="primary" className="shrink-0" loading={busy && step === 'identify'}
                      icon={<MessageCircle size={15} />} onClick={sendCode}>
                      {step === 'verify' ? 'Resend' : 'Verify'}
                    </Button>
                  )}
                </div>
              </Field>

              {/* The code, and nothing else, until it is accepted. */}
              {step === 'verify' && (
                <Field label="Enter the code" required error={errors.code}
                  hint={devCode ? `Development code: ${devCode}` : `Sent on WhatsApp to ${mobile}.`}>
                  <div className="flex items-start gap-2">
                    <Input id="rf-code" aria-label="Enter the code" inputMode="numeric" maxLength={6} value={code}
                      invalid={!!errors.code} className="max-w-40" placeholder="6-digit code"
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setErrors((x) => ({ ...x, code: undefined })); }} />
                    <Button variant="primary" className="shrink-0" loading={busy} icon={<Check size={15} />} onClick={verify}>
                      Confirm
                    </Button>
                  </div>
                </Field>
              )}

              {/* Where they farm — only once we know whose number this is. */}
              {verified && (
                <>
                  <FarmerPlaceFields value={place} errors={errors} onChange={(patch) => {
                    setPlace((p) => ({ ...p, ...patch }));
                    setErrors((x) => {
                      const n = { ...x };
                      if ('stateCode' in patch) delete n.state;
                      if ('districtId' in patch || 'districtText' in patch) delete n.district;
                      if ('village' in patch) delete n.village;
                      if ('pincode' in patch) delete n.pincode;
                      return n;
                    });
                  }} />

                  <div>
                    <Checkbox checked={consent} onCheckedChange={(c) => { setConsent(!!c); setErrors((x) => ({ ...x, consent: undefined })); }}
                      label="I agree to be registered as a farmer, and that Green Bay Biotech may store these details and contact me about its products and services." />
                    {errors.consent && <p className="mt-1 type-caption text-danger-600">{errors.consent}</p>}
                  </div>

                  <Button variant="primary" block loading={busy} onClick={submit}>Complete registration</Button>
                </>
              )}

              {!verified && (
                <p className="text-center type-caption text-muted">
                  Already registered?{' '}
                  <button type="button" className="text-brand-700 underline" onClick={() => navigate('/login')}>Sign in</button>
                </p>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
